import { baseUrl } from 'src';
import { OrganizationRepresentation, OrganizationDomainRepresentation } from 'src/query/orgs';
import keycloak from '../keycloak';

// Create Org
export async function createOrg(orgDetails: { name: string; displayName: string }): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orgDetails),
  });
}

export async function createRoleForOrg(roleDetails: { orgId: string; roleName: string }): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs/${roleDetails.orgId}/roles`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: roleDetails.roleName }),
  });
}

export async function addUserToRole(userId: string, orgId: string, roleName: string): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs/${orgId}/roles/${roleName}/users/${userId}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
}

// Create Deployment for Organization
export async function createDeployment(deploymentDetails: {
  name: string;
  organization_id: string;
  region: string;
}): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/deployments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(deploymentDetails),
  });
}

export async function deleteDeployment({ deploymentId }: { deploymentId: string }): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/deployments/${deploymentId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
}

export async function createCluster(clusterDetails: {
  name: string;
  region: string;
  domain: string;
  price_id: string;
  org_id: string;
}): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/clusters`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(clusterDetails),
  });
}

export async function createClusterDedicatedDeployment(
  deployment: { name: string },
  clusterId: string
): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/clusters/${clusterId}/deployments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(deployment),
  });
}

export async function deleteCluster({ clusterId }: { clusterId: string }): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/clusters/${clusterId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
}

// Invite user to an org
export async function inviteUserToOrg({
  orgId,
  inviteDetails,
}: {
  orgId: string;
  inviteDetails: {
    email: string;
    inviterId: string;
    roles: {
      [roleName: string]: string;
    };
  };
}): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs/${orgId}/invitations`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      send: true,
      ...inviteDetails,
    }),
  });
}

// Get roles for an organization for specific member
export async function getRolesForMemberInOrg({ userId, orgId }: { userId: string; orgId: string }): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/users/${userId}/orgs/${orgId}/roles`, {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
}

// Check for a specific role for a user within organization
export async function checkMemberRoleInOrg({
  roleName,
  orgId,
  userId,
}: {
  roleName: string;
  orgId: string;
  userId: string;
}): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs/${orgId}/roles/${roleName}/users/${userId}`, {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
}

export async function editOrg({ org }: { org: OrganizationRepresentation }): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs/${org.id}`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(org),
  });
}

type UniqueNameResponse = {
  name: string;
  available: boolean;
};

export async function isDeploymentNameUnique({
  deploymentName,
}: {
  deploymentName: string;
}): Promise<UniqueNameResponse> {
  const resp = await fetch(`${baseUrl}/${keycloak.realm}/deployments/name-available?name=${deploymentName}`, {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
  return await resp.json();
}

export async function isClusterNameUnique({ clusterName }: { clusterName: string }): Promise<UniqueNameResponse> {
  const resp = await fetch(`${baseUrl}/${keycloak.realm}/clusters/name-available?name=${clusterName}`, {
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
    },
  });
  return await resp.json();
}

export async function verifyDomain({
  orgId,
  domain,
}: {
  orgId: string;
  domain: OrganizationDomainRepresentation;
}): Promise<Response> {
  return fetch(`${baseUrl}/${keycloak.realm}/orgs/${orgId}/domains/${domain.domain_name}/verify`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

const Orgs = {
  checkMemberRoleInOrg,
  createDeployment,
  createClusterDedicatedDeployment,
  createOrg,
  addUserToRole,
  createRoleForOrg,
  deleteDeployment,
  editOrg,
  getRolesForMemberInOrg,
  isDeploymentNameUnique,
  isClusterNameUnique,
  inviteUserToOrg,
  verifyDomain,
  createCluster,
  deleteCluster,
};

export default Orgs;
